import { CompositeScreenProps } from '@react-navigation/native'
import { IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import React, { useEffect, useRef, useState } from 'react'
import { ImageBackground, ScrollView, StyleSheet } from 'react-native'

import { EmergingCoaches } from 'partials/EmergingCoaches/EmergingCoaches'
import { NavMenuButton } from 'partials/MobileNav/types'
import { OtherMembers } from 'partials/OtherMembers'
import { Page } from 'partials/Page'
import { RecentWorkouts } from 'partials/RecentWorkouts'
import { TrendingWorkouts } from 'partials/TrendingWorkouts'
import { WorkoutTagsModal } from 'partials/WorkoutTags'
import { WorkoutTagsFilter } from 'partials/WorkoutTagsFilter'

import { Box } from 'widgets/Box'
import { FloatingSearchBar } from 'widgets/FloatingUserSearchBar'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'
import { Title } from 'widgets/Title'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDiscoverContent } from 'hooks/useDiscoverContent'
import { useEmergingCoaches } from 'hooks/useEmergingCoaches'
import { useInfinitePosts } from 'hooks/useInfinitePosts'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { useOtherMembers } from 'hooks/useOtherMembers'
import { useQueryClient } from 'hooks/useQueryClient'
import { useTrendingWorkouts } from 'hooks/useTrendingWorkouts'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'
import { fetchUserByUserId } from 'utils/queries'
import commonStyles from 'utils/styles/commonStyles'

import { useFocusMode } from 'contexts/FocusModeContext'

import coachesImage from 'assets/images/coaches.png'
import workoutsImage from 'assets/images/workouts.png'

import { UserType } from 'types/user'

const styles = StyleSheet.create({
  pageBox: {
    overflow: 'hidden',
  },
  pressableBox: {
    width: '48%',
  },
  pressableBoxTitle: {
    position: 'absolute',
    bottom: 4,
    left: 16,
  },
})

export const ExplorePage = ({ navigation }: CompositeScreenProps<any, any>) => {
  const { width: maxWidth } = useMaxWidth()
  const [isTagsModalVisible, setIsTagsModalVisible] = useState<boolean>(false)
  const [tagFilter, setTagFilter] = useState<any>(undefined)
  const focusMode = useFocusMode()

  const handleFilterPress = () => {
    setIsTagsModalVisible(true)
  }

  const filterButton: NavMenuButton = {
    iconName: 'filter',
    onPress: handleFilterPress,
  }

  const queryClient = useQueryClient()
  const theme = useTheme()
  const { data: currentUser } = useCurrentUser()

  useEffect(() => {
    queryClient.invalidateQueries('subscriptions')
  }, [queryClient])

  const { data: postHome, isLoading: isLoadingPosts } = useInfinitePosts({
    order: 'created',
    filter: 'Workouts',
  })
  const filteredPosts = useRef(Array.from(postHome))

  useEffect(() => {
    const filterPosts = async () => {
      filteredPosts.current = postHome.filter(
        (p) =>
          (p.isPending === false || p.isPending === undefined) &&
          (p.isArchived === false || p.isArchived === undefined),
      )

      if (currentUser?.id) {
        if (currentUser?.blockedUserIds && currentUser?.blockedUserIds.length > 0) {
          filteredPosts.current = filteredPosts.current.filter(
            (post) => !currentUser?.blockedUserIds?.includes(post.creator.id as string),
          )
        }

        const creatorIds = filteredPosts.current.map((post) => post.creator.id as string)
        const uniqueCreatorIds = creatorIds.filter(
          (value, index, self) => self.indexOf(value) === index,
        )

        const fullCreators: UserType[] = []
        const creatorPromises = uniqueCreatorIds.map((id: string) =>
          fetchUserByUserId({
            userId: id,
          }).then((creator) => {
            if (creator) {
              fullCreators.push(creator)
            }
          }),
        )

        await Promise.all(creatorPromises)

        fullCreators.forEach((fullCreator) => {
          if (fullCreator?.blockedUserIds && fullCreator?.blockedUserIds?.length > 0) {
            if (fullCreator.blockedUserIds.indexOf(currentUser.id as string) > -1) {
              filteredPosts.current = filteredPosts.current.filter(
                (post) => post.creator.id !== fullCreator.id,
              )
            }
          }
        })
      }
    }

    if (filteredPosts.current) {
      filterPosts()
    }
  }, [postHome, isLoadingPosts, currentUser])

  const handleUpspacesPress = () => {
    navigation.navigate(links.TRAINERS_PAGE)
  }

  const handleWorkoutsPress = () => {
    navigation.navigate(links.WORKOUTS_PAGE)
  }

  const { data: creator, isLoading: isLoadingCreator } = useUser({
    userId: focusMode?.group?.trainer?.id,
  })
  const { data: members, isLoading: isLoadingMembers } = useOtherMembers(focusMode?.group)

  const { data: discoverContent, isLoading: isLoadingDiscoverContent } = useDiscoverContent()
  const { data: emergingCoaches, isLoading: isLoadingEmergingCoaches } = useEmergingCoaches()
  const { data: trendingWorkouts, isLoading: isLoadingTrendingWorkouts } = useTrendingWorkouts()

  const { data: recentWorkouts, isLoading: isLoadingRecentWorkouts } = useInfinitePosts({
    creatorId: focusMode.group ? focusMode.group?.trainer.id : undefined,
    limit: focusMode.group ? undefined : 25,
    order: 'created',
    filter: 'Workouts',
  })

  return (
    <Page
      backgroundColor='white'
      title='Explore'
      withNav
      withBack
      withExitFocusMode
      navigation={navigation}
      navbarRightButtons={[filterButton]}
      isLoading={isLoadingPosts}
    >
      {tagFilter ? (
        <WorkoutTagsFilter filters={tagFilter} handleEditFilter={handleFilterPress} />
      ) : (
        <>
          <Box
            testID='CoachSearchRow'
            zIndex={2}
            padding={{ left: theme.spacing.md, right: theme.spacing.md }}
          >
            <FloatingSearchBar
              shouldPadTop={!IS_WEB}
              searchPrompt='Search for Friends by first name'
              width={maxWidth - theme.spacing.md * 2}
            />
          </Box>
          <Box flex={1}>
            <ScrollView>
              <Box
                testID='ExplorePageContentContainer'
                flexDirection='column'
                width='100%'
                margin={{ top: 'sm', left: 'sm', right: 'sm', bottom: 'md' }}
              >
                {!focusMode.group && (
                  <>
                    <Title bold>For You</Title>
                    <Box
                      testID='ForYouRow'
                      flexDirection='row'
                      justifyContent='flex-start'
                      width='100%'
                      margin={{ top: 'sm', bottom: 'md' }}
                      padding={{ bottom: 'smd' }}
                    >
                      <Pressable
                        testID='Groups'
                        style={styles.pressableBox}
                        onPress={handleUpspacesPress}
                      >
                        <Box
                          height={100}
                          borderRadius='md'
                          margin={{ right: 'md' }}
                          style={styles.pageBox}
                        >
                          <ImageBackground
                            source={coachesImage}
                            resizeMode='cover'
                            style={commonStyles.flex}
                          />
                          <Text bold color='textInverted' style={styles.pressableBoxTitle}>
                            Groups
                          </Text>
                        </Box>
                      </Pressable>
                      <Pressable
                        testID='Workouts'
                        style={styles.pressableBox}
                        onPress={handleWorkoutsPress}
                      >
                        <Box height={100} borderRadius='md' style={styles.pageBox}>
                          <ImageBackground
                            source={workoutsImage}
                            resizeMode='cover'
                            style={commonStyles.flex}
                          />
                          <Text bold color='textInverted' style={styles.pressableBoxTitle}>
                            Workouts
                          </Text>
                        </Box>
                      </Pressable>
                    </Box>
                  </>
                )}
                {focusMode.group && (
                  <OtherMembers
                    program={focusMode.group}
                    creator={creator}
                    members={members}
                    isLoadingCreator={isLoadingCreator}
                    isLoadingMembers={isLoadingMembers}
                  />
                )}
                <RecentWorkouts
                  title='Newest Workouts'
                  recentWorkouts={recentWorkouts}
                  isLoadingRecentWorkouts={isLoadingRecentWorkouts}
                  focusModeProgram={focusMode?.group}
                />
                {!focusMode.group && (
                  <EmergingCoaches
                    discoverContent={discoverContent}
                    isLoadingDiscoverContent={isLoadingDiscoverContent}
                    emergingCoaches={emergingCoaches}
                    isLoadingEmergingCoaches={isLoadingEmergingCoaches}
                  />
                )}
                {!focusMode.group && (
                  <TrendingWorkouts
                    discoverContent={discoverContent}
                    isLoadingDiscoverContent={isLoadingDiscoverContent}
                    trendingWorkouts={trendingWorkouts}
                    isLoadingTrendingWorkouts={isLoadingTrendingWorkouts}
                  />
                )}
              </Box>
            </ScrollView>
          </Box>
        </>
      )}

      <WorkoutTagsModal
        isFilter
        isVisible={isTagsModalVisible}
        onClose={(selection: any) => {
          setIsTagsModalVisible(false)
          setTagFilter(selection)
        }}
      />
    </Page>
  )
}
